import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useRef } from 'react';
import type { ConsumptionByDeviceType } from '../../../api/wattelzce-demo';
import { useI18n } from '../../../i18n';

interface ConsumptionByDeviceChartProps {
  data: ConsumptionByDeviceType[];
  loading?: boolean;
}

function ConsumptionByDeviceChart({ data, loading }: ConsumptionByDeviceChartProps) {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const { t } = useI18n();

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-80 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  const colors = ['#059669', '#2563eb', '#dc2626', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

  const chartData = data.map((d, index) => ({
    name: d.device_type,
    y: d.energy,
    percentage: d.percentage,
    cost: d.cost,
    co2: d.co2,
    color: colors[index % colors.length],
  }));

  const options: Highcharts.Options = {
    chart: {
      type: 'pie',
      height: 320,
      style: {
        fontFamily: 'inherit',
      },
    },
    title: {
      text: t('consumptionByDevice'),
      style: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#1f2937',
      },
    },
    subtitle: {
      text: t('consumptionByDeviceSubtitle'),
      style: {
        fontSize: '12px',
        color: '#6b7280',
      },
    },
    tooltip: {
      pointFormat: `<b>{point.y:,.1f} ${t('kwh')}</b> ({point.percentage:.1f}%)<br/>${t('cost')}: {point.cost:,.2f} ${t('euro')}<br/>${t('co2')}: {point.co2:,.1f} ${t('kg')}`,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e5e7eb',
      borderRadius: 8,
    },
    plotOptions: {
      pie: {
        innerSize: '50%',
        allowPointSelect: true,
        cursor: 'pointer',
        borderRadius: 4,
        borderWidth: 2,
        borderColor: '#ffffff',
        dataLabels: {
          enabled: true,
          format: '<b>{point.name}</b><br/>{point.percentage:.1f}%',
          style: {
            fontSize: '11px',
            fontWeight: '500',
            color: '#374151',
            textOutline: 'none',
          },
          distance: 20,
        },
        showInLegend: true,
      },
    },
    legend: {
      enabled: true,
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      itemStyle: {
        fontSize: '11px',
        fontWeight: '500',
        color: '#374151',
      },
    },
    series: [{
      type: 'pie',
      name: t('energy'),
      data: chartData,
    }],
    credits: {
      enabled: false,
    },
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
      />
    </div>
  );
}

export default ConsumptionByDeviceChart;
